'use client'

import cn from 'clsx'
import { useRect } from 'hamo'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Button } from '~/components/button'
import { Image } from '~/components/image'
import { SplitText } from '~/components/split-text'
import { useScrollTrigger } from '~/hooks/use-scroll-trigger'
import { clamp, mapRange } from '~/libs/maths'
import s from './demo.module.css'

const ROWS = Array.from({ length: 7 }, (_, i) => ({
  id: i,
  src: `/images/rows/${i + 1}.png`,
  random: mapRange(0, 1, Math.random(), 0.75, 1),
}))

const Row = forwardRef(({ src }, ref) => {
  const rowRef = useRef(null)
  const skeletonRef = useRef(null)

  const [setRectRef, rect] = useRect()

  useImperativeHandle(
    ref,
    () => ({
      skeletonRef,
      rowRef,
      rect,
    }),
    [rect]
  )

  return (
    <div className={s.row} ref={rowRef}>
      <div className="dr-h-47" ref={skeletonRef} />
      <div
        ref={(node) => {
          setRectRef(node)
        }}
        className={cn(
          'flex dr-gap-10 dr-p-11 items-center bg-secondary absolute top-0 left-0 right-0 dr-h-47',
          s.rowInner
        )}
      >
        <Image src={src} alt="" fill desktopSize="28vw" />
      </div>
    </div>
  )
})

export function DemoDesktop() {
  const rowsRefs = useRef([])
  const modalRef = useRef()
  const draftRef = useRef()
  const rowsWrapperRef = useRef()

  const [setDraftRectRef, draftRect] = useRect({})

  // const [setFilterRectRef, filterRect] = useRect({
  //   ignoreSticky: true,
  // })

  // const [setResponseRectRef, responseRect] = useRect({
  //   ignoreSticky: true,
  // })

  const splittedDraftTextRef = useRef()

  const [setRectRef, rect] = useRect()

  useScrollTrigger({
    rect,
    start: 'top top',
    end: 'bottom bottom',
    onProgress: ({ progress }) => {
      const step1Progress = clamp(0, mapRange(0, 0.25, progress, 0, 1), 1)
      const step2Progress = clamp(0, mapRange(0.25, 0.5, progress, 0, 1), 1)
      const step3Progress = clamp(0, mapRange(0.5, 0.6, progress, 0, 1), 1)
      const step4Progress = clamp(0, mapRange(0.6, 1, progress, 0, 1), 1)

      const rowsToAnimate = rowsRefs.current.slice(1, 5)
      rowsToAnimate.forEach((row, i) => {
        if (step1Progress > 0) {
          const initialHeight = row.rect.height
          const newHeight = initialHeight * (1 - step1Progress)
          row.skeletonRef.current.style.height = `${newHeight}px`
          row.rowRef.current.style.transform = `translateX(${step1Progress * 60 * ROWS[i].random}vw) translateY(${step1Progress * 5 * (i - 2)}vw) translateZ(0)`
          row.rowRef.current.style.position = 'relative'
          row.rowRef.current.style.zIndex = 1
        } else {
          row.rowRef.current.style.transform = `translateZ(0)`
          row.rowRef.current.style.removeProperty('position')
          row.rowRef.current.style.removeProperty('z-index')
        }
      })

      if (step3Progress === 0) {
        draftRef.current.style.opacity = 0
        rowsWrapperRef.current.style.opacity = 1
      } else {
        draftRef.current.style.opacity = 1
        rowsWrapperRef.current.style.opacity = 0
      }

      if (step2Progress > 0) {
        modalRef.current.style.overflow = 'clip'
      } else {
        modalRef.current.style.removeProperty('overflow')
      }

      if (step2Progress > 0 && step2Progress < 1) {
        modalRef.current.style.height = `${mapRange(0, 1, step2Progress, 3 * rowsToAnimate[0].rect.height, rowsToAnimate[0].rect.height)}px`
      } else if (step3Progress > 0) {
        modalRef.current.style.height = `${mapRange(0, 1, step3Progress, rowsToAnimate[0].rect.height, draftRect.height)}px`
      } else {
        modalRef.current.style.removeProperty('height')
      }

      const threshold = Math.floor(
        step4Progress * splittedDraftTextRef.current?.chars?.length
      )

      splittedDraftTextRef.current?.chars?.forEach((char, index) => {
        char.style.opacity = index < threshold ? 1 : 0
        char.classList.toggle('active', index === threshold - 1)
      })
    },
  })

  // useScrollTrigger({
  //   rect: responseRect,
  //   start: 'top top',
  //   end: 'bottom bottom',
  //   onProgress: ({ progress }) => {
  //     const heightProgress = clamp(0, mapRange(0, 0.33, progress, 0, 1), 1)

  //     draftRef.current.style.height = `${heightProgress * draftRect.height}px`
  //     draftRef.current.style.opacity = Math.ceil(progress)

  //     const threshold = Math.floor(
  //       mapRange(0.33, 1, progress, 0, 1) *
  //         splittedDraftTextRef.current.chars.length
  //     )

  //     splittedDraftTextRef.current?.chars?.forEach((char, index) => {
  //       char.style.opacity = index < threshold ? 1 : 0
  //     })
  //   },
  // })

  return (
    <section
      className={cn(s.demo, 'overflow-clip desktop-only')}
      ref={setRectRef}
    >
      <div className="dr-gap-110 flex justify-center">
        <div className="dr-w-386 shrink-0 flex flex-col">
          <div
            className="w-full dr-h-2160 flex flex-col justify-between mb-[-50vh]"
            // ref={setFilterRectRef}
          >
            <div className="h-[100vh] flex items-center justify-center sticky top-0 pointer-events-none">
              <div className="pointer-events-auto">
                <h3 className="h4-l dr-mb-8">Cora screens your email</h3>
                <p className="p-xxl dr-mb-25">
                  Cora knows what’s important to you and the types of emails you
                  need to respond to. It keeps those messages in your inbox for
                  you to see ASAP.
                </p>
                <Button
                  href="https://cora.computer/users/sign_up"
                  label="Start your free trial"
                />
              </div>
            </div>
          </div>
          <div
            className="w-full dr-h-2160 flex flex-col justify-between"
            // ref={setResponseRectRef}
          >
            <div className="h-[100vh] flex items-center justify-center sticky top-0 pointer-events-none">
              <div className="pointer-events-auto">
                <h3 className="h4-l dr-mb-8">
                  Cora drafts responses in your voice
                </h3>
                <p className="p-xxl dr-mb-25">
                  Whenever Cora has enough context from your email history to
                  draft a great response for you, it does.
                </p>
                <Button
                  label="Get started"
                  href="https://cora.computer/users/sign_up"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="dr-w-644">
          <div className="sticky top-0 h-[100vh] flex justify-center items-center">
            <div
              ref={modalRef}
              className=" relative w-full bg-secondary dr-rounded-10 text-[#2B2B2B] font-switzer font-[600] dr-text-6 floating-container"
            >
              <div className="flex flex-col" ref={rowsWrapperRef}>
                {ROWS.map((row, i) => (
                  <Row
                    key={row.id}
                    {...row}
                    ref={(node) => {
                      rowsRefs.current[i] = node
                    }}
                  />
                ))}
              </div>
              <div
                className="absolute left-0 right-0 top-0 dr-px-20 dr-py-20 flex flex-col dr-gap-11"
                ref={(node) => {
                  draftRef.current = node
                  setDraftRectRef(node)
                }}
              >
                <div className="flex dr-gap-6 flex-col">
                  <h4 className="font-[500] dr-text-17">
                    Data-sharing agreement signature
                  </h4>
                  <div className="flex items-center dr-gap-7">
                    <div className="aspect-square dr-w-38 dr-h-38  dr-rounded-full rounded-full relative overflow-clip">
                      <Image src="/images/doctor.png" alt="" fill />
                    </div>
                    <div>
                      <h5 className="font-[600] dr-text-16">Dr. Lila Mensah</h5>
                      <span className="font-[400] dr-text-16">
                        lila.mensah@medinsight.ai
                      </span>
                    </div>
                  </div>
                </div>
                <p className="dr-text-16 font-[400]">
                  Legal finalized the DSA in DocuSign. Could you review the
                  privacy clauses and sign so we stay on track for the 5/20 data
                  pull? Let me know if anything looks off.
                </p>
                <div className="subtle-shadow dr-rounded-bl-6 dr-rounded-br-6 overflow-clip">
                  <div className="dr-h-458 flex flex-col dr-text-17">
                    <div className="dr-py-11 dr-px-16 bg-[#F2F6FC] text-[#CF372D]">
                      Cora Draft
                    </div>
                    <div className="dr-px-16 dr-pb-13 grow flex flex-col justify-between">
                      <div>
                        <div className="dr-h-40 flex items-center border-b border-b-solid border-[#DADADA] font-[600]">
                          {'From: Samira <samira@insightpeak.co>'}
                        </div>
                        <div className="dr-h-40 flex items-center border-b border-b-solid border-[#DADADA]  font-[600]">
                          {'To: Naveen <naveen@monologue.to>'}
                        </div>
                        <div className="dr-h-40 flex items-center font-[600] dr-mb-24">
                          Re: Monologue Proposal–let’s sign
                        </div>
                        <div>
                          <SplitText
                            type="chars, words"
                            className="font-[500]"
                            ref={splittedDraftTextRef}
                          >
                            Loved the deck and the way Monologue personalizes
                            your responses. Spells! What a great name. We’re
                            excited to move forward and would like to hash out
                            next steps on a quick call tomorrow at 11am ET—does
                            that work for you? Let me know and I’ll send a
                            calendar invite.
                            <br />
                            <br />
                            <span className="font-[600]">Best,</span>
                            <br />
                            <span className="font-[600]">Samira</span>
                          </SplitText>
                          <br />
                        </div>
                      </div>
                      <div className="dr-h-36 dr-rounded-18 bg-[#0B57D0] self-start text-[#fff] flex">
                        <div className="flex items-center h-full dr-px-20 border-r border-r-solid border-r-[#062E6F] dr-text-17 font-[500]">
                          Send
                        </div>
                        <div className="flex items-center h-full dr-pl-10 dr-pr-14 dr-text-17 font-[500]">
                          ▼
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
